"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAnnouncementStore } from "../model/use-announcement-store";
import { useDragTooltip } from "../model/use-drag-tooltip";
interface FabGroupProps {
  readonly isAnnouncementVisible: boolean;
  readonly feedbackEnabled?: boolean;
  readonly feedbackDisplayMode?: "sidebar" | "modal";
  readonly isSidebarOpen: boolean;
  readonly isModalOpen: boolean;
  readonly onSparkleClick: () => void;
  readonly onFeedbackClick: () => void;
  /** Whether FABs should be hidden (scroll-down state) */
  readonly isHidden?: boolean;
  /** Current corner position for the FAB group (always bottom-right) */
  readonly corner?: "bottom-right";
  /** CSS positioning style from useDraggablePosition */
  readonly positionStyle?: React.CSSProperties;
  /** Called when drag ends */
  readonly onDragEnd?: (event: MouseEvent | TouchEvent | PointerEvent, info: {
    point: {
      x: number;
      y: number;
    };
  }) => void;
  /** Whether drag is enabled (false on mobile) */
  readonly isDragEnabled?: boolean;
  /** Whether the current feature is backed by an addon */
  readonly isAddonFeature?: boolean;
  /** Unread feedback-thread count — renders a badge on the feedback FAB when > 0 */
  readonly feedbackUnreadCount?: number;
}
const IDLE_TIMEOUT = 4000;
const IDLE_OPACITY = 0.5;

/** Off-screen translation for hiding FABs (always bottom-right) */
const HIDE_TRANSFORM = {
  x: 100,
  y: 40
};

/** 6-dot grip handle SVG (2x3 grid) */
function GripDots() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="10" height="16" viewBox="0 0 10 16" fill="white" aria-hidden="true"><circle cx="3" cy="2" r="1.5" /><circle cx="7" cy="2" r="1.5" /><circle cx="3" cy="8" r="1.5" /><circle cx="7" cy="8" r="1.5" /><circle cx="3" cy="14" r="1.5" /><circle cx="7" cy="14" r="1.5" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export function FeatureAnnouncementFabGroup(t0) {
  const $ = _c(54);
  const {
    isAnnouncementVisible,
    feedbackEnabled: t1,
    feedbackDisplayMode: t2,
    isSidebarOpen,
    isModalOpen,
    onSparkleClick,
    onFeedbackClick,
    isHidden: t3,
    positionStyle,
    onDragEnd,
    isDragEnabled: t4,
    isAddonFeature: t5,
    feedbackUnreadCount: t6
  } = t0;
  const feedbackEnabled = t1 === undefined ? false : t1;
  const feedbackDisplayMode = t2 === undefined ? "sidebar" : t2;
  const isHidden = t3 === undefined ? false : t3;
  const isDragEnabled = t4 === undefined ? false : t4;
  const isAddonFeature = t5 === undefined ? false : t5;
  const feedbackUnreadCount = t6 === undefined ? 0 : t6;
  const isCapturingScreen = useAnnouncementStore(_temp);
  const hasSeenAnnouncement = useAnnouncementStore(_temp2);
  const showPulse = !hasSeenAnnouncement && !isModalOpen && !isSidebarOpen;
  const shouldShiftLeft = isSidebarOpen && feedbackDisplayMode === "sidebar";
  const [isIdle, setIsIdle] = useState(false);
  const idleTimer = useRef(null);
  const containerRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const wasDraggingRef = useRef(false);
  const {
    showTooltip,
    dismissTooltip
  } = useDragTooltip(isDragEnabled);
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsIdle(false);
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
      if (typeof window !== "undefined" && window.innerWidth >= 768) {
        idleTimer.current = setTimeout(() => setIsIdle(true), IDLE_TIMEOUT);
      }
    };
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const resetIdleTimer = t7;
  const isPanelOpen = isModalOpen || isSidebarOpen;
  const [wasPanelOpen, setWasPanelOpen] = useState(isPanelOpen);
  if (isPanelOpen !== wasPanelOpen) {
    setWasPanelOpen(isPanelOpen);
    setIsIdle(false);
  }
  let t8;
  let t9;
  if ($[1] !== isPanelOpen) {
    t8 = () => {
      if (!isPanelOpen && window.innerWidth >= 768) {
        idleTimer.current = setTimeout(() => setIsIdle(true), IDLE_TIMEOUT);
      }
      return () => {
        if (idleTimer.current) {
          clearTimeout(idleTimer.current);
        }
      };
    };
    t9 = [isPanelOpen];
    $[1] = isPanelOpen;
    $[2] = t8;
    $[3] = t9;
  } else {
    t8 = $[2];
    t9 = $[3];
  }
  useEffect(t8, t9);
  const shouldHide = isHidden && !isModalOpen && !isSidebarOpen;
  const showGripDots = isDragEnabled && isHovered && !isDragging && !shouldHide && !isIdle;
  const showDragTooltip = showTooltip && !shouldHide && !isDragging;
  const t10 = shouldShiftLeft ? "max-[480px]:opacity-0 max-[480px]:pointer-events-none" : "";
  let t11;
  if ($[4] !== t10) {
    t11 = ["fixed z-1000 flex items-center gap-3", t10];
    $[4] = t10;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  const t12 = t11.join(" ");
  const t13 = isDragEnabled ? "grab" : undefined;
  const t14 = isCapturingScreen ? "hidden" : undefined;
  let t15;
  if ($[6] !== positionStyle || $[7] !== t13 || $[8] !== t14) {
    t15 = {
      ...positionStyle,
      cursor: t13,
      visibility: t14
    };
    $[6] = positionStyle;
    $[7] = t13;
    $[8] = t14;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  const t16 = shouldHide ? HIDE_TRANSFORM.x : shouldShiftLeft ? -400 : 0;
  const t17 = shouldHide ? HIDE_TRANSFORM.y : 0;
  const t18 = shouldHide ? 0 : isIdle ? IDLE_OPACITY : 1;
  let t19;
  if ($[10] !== t16 || $[11] !== t17 || $[12] !== t18) {
    t19 = {
      x: t16,
      y: t17,
      opacity: t18
    };
    $[10] = t16;
    $[11] = t17;
    $[12] = t18;
    $[13] = t19;
  } else {
    t19 = $[13];
  }
  let t20;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      type: "spring",
      damping: 25,
      stiffness: 300
    };
    $[14] = t20;
  } else {
    t20 = $[14];
  }
  let t21;
  if ($[15] !== dismissTooltip) {
    t21 = () => {
      setIsDragging(true);
      wasDraggingRef.current = true;
      dismissTooltip();
    };
    $[15] = dismissTooltip;
    $[16] = t21;
  } else {
    t21 = $[16];
  }
  let t22;
  if ($[17] !== onDragEnd) {
    t22 = (event, info) => {
      setIsDragging(false);
      onDragEnd?.(event, info);
      requestAnimationFrame(() => {
        wasDraggingRef.current = false;
      });
    };
    $[17] = onDragEnd;
    $[18] = t22;
  } else {
    t22 = $[18];
  }
  let t23;
  let t24;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      setIsHovered(true);
      resetIdleTimer();
    };
    t24 = () => {
      setIsHovered(false);
    };
    $[19] = t23;
    $[20] = t24;
  } else {
    t23 = $[19];
    t24 = $[20];
  }
  let t25;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      scale: 1.05,
      cursor: "grabbing"
    };
    $[21] = t25;
  } else {
    t25 = $[21];
  }
  let t26;
  if ($[22] !== showGripDots) {
    t26 = showGripDots && <motion.div className="absolute left-1/2 -translate-x-1/2 pointer-events-none" style={{
      bottom: "100%",
      marginBottom: 6
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 0.4
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }}><GripDots /></motion.div>;
    $[22] = showGripDots;
    $[23] = t26;
  } else {
    t26 = $[23];
  }
  let t27;
  if ($[24] !== t26) {
    t27 = <AnimatePresence>{t26}</AnimatePresence>;
    $[24] = t26;
    $[25] = t27;
  } else {
    t27 = $[25];
  }
  let t28;
  if ($[26] !== isAddonFeature || $[27] !== isAnnouncementVisible || $[28] !== onSparkleClick || $[29] !== showPulse) {
    t28 = isAnnouncementVisible && <motion.button type="button" aria-label="View feature announcement" onClick={() => {
      if (!wasDraggingRef.current) {
        onSparkleClick();
      }
    }} className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#6366f1] text-white shadow-lg transition-transform hover:scale-[1.08] active:scale-[0.97]" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0,
      opacity: 0
    }}>{showPulse && <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-red-500 border-2 border-white" /></span>}{isAddonFeature && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-amber-500 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase leading-none text-white shadow-xs">Add-on</span>}<svg width="24" height="24" viewBox="0 0 48 48" fill="currentColor" aria-hidden="true"><path d="M24 4l3 10 10 3-10 3-3 10-3-10-10-3 10-3z" /><path d="M38 28l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" opacity="0.7" /><path d="M12 30l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z" opacity="0.7" /></svg></motion.button>;
    $[26] = isAddonFeature;
    $[27] = isAnnouncementVisible;
    $[28] = onSparkleClick;
    $[29] = showPulse;
    $[30] = t28;
  } else {
    t28 = $[30];
  }
  let t29;
  if ($[31] !== t28) {
    t29 = <AnimatePresence>{t28}</AnimatePresence>;
    $[31] = t28;
    $[32] = t29;
  } else {
    t29 = $[32];
  }
  let t30;
  if ($[33] !== feedbackEnabled || $[34] !== feedbackUnreadCount || $[35] !== onFeedbackClick) {
    t30 = feedbackEnabled && <motion.button type="button" aria-label="Send feedback" onClick={() => {
      if (!wasDraggingRef.current) {
        onFeedbackClick();
      }
    }} className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#6366f1] text-white shadow-lg transition-transform hover:scale-[1.08] active:scale-[0.97]" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0,
      opacity: 0
    }}>{feedbackUnreadCount > 0 && <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white border-2 border-white">{feedbackUnreadCount > 9 ? "9+" : feedbackUnreadCount}</span>}<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></motion.button>;
    $[33] = feedbackEnabled;
    $[34] = feedbackUnreadCount;
    $[35] = onFeedbackClick;
    $[36] = t30;
  } else {
    t30 = $[36];
  }
  let t31;
  if ($[37] !== t30) {
    t31 = <AnimatePresence>{t30}</AnimatePresence>;
    $[37] = t30;
    $[38] = t31;
  } else {
    t31 = $[38];
  }
  let t32;
  if ($[39] !== showDragTooltip) {
    t32 = showDragTooltip && <motion.div className="absolute top-1/2 -translate-y-1/2 pointer-events-none whitespace-nowrap rounded-full bg-gray-900/85 backdrop-blur-xs px-3 py-1.5 text-xs font-medium text-white shadow-lg" style={{
      right: "100%",
      marginRight: 12
    }} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.9
    }} transition={{
      duration: 0.2
    }}>Drag to reposition</motion.div>;
    $[39] = showDragTooltip;
    $[40] = t32;
  } else {
    t32 = $[40];
  }
  let t33;
  if ($[41] !== t32) {
    t33 = <AnimatePresence>{t32}</AnimatePresence>;
    $[41] = t32;
    $[42] = t33;
  } else {
    t33 = $[42];
  }
  let t34;
  if ($[43] !== isDragEnabled || $[44] !== t12 || $[45] !== t15 || $[46] !== t19 || $[47] !== t21 || $[48] !== t22 || $[49] !== t27 || $[50] !== t29 || $[51] !== t31 || $[52] !== t33) {
    t34 = <motion.div ref={containerRef} className={t12} style={t15} animate={t19} transition={t20} drag={isDragEnabled} dragMomentum={false} dragElastic={0.1} onDragStart={t21} onDragEnd={t22} onPointerEnter={t23} onPointerLeave={t24} onPointerMove={resetIdleTimer} whileDrag={t25}>{t27}{t29}{t31}{t33}</motion.div>;
    $[43] = isDragEnabled;
    $[44] = t12;
    $[45] = t15;
    $[46] = t19;
    $[47] = t21;
    $[48] = t22;
    $[49] = t27;
    $[50] = t29;
    $[51] = t31;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  return t34;
}
function _temp2(s_0) {
  return s_0.hasSeenAnnouncement;
}
function _temp(s) {
  return s.isCapturingScreen;
}